import { loadMarkdown } from "./lazy.js";

// Distance from the bottom past which we stop auto-scrolling on new
// messages, so a user reading history isn't yanked back down. Mirrors
// transcript.ts's SCROLL_THRESHOLD.
const SCROLL_THRESHOLD = 40;

// The text-chat thread + composer, rendered inside the widget popup when
// mode="text". Shares the visual language of the voice transcript
// (role-tagged bubbles) but adds a message composer and owns no session
// state — the element drives it via the TextChatController.

const CHAT_CSS = `
  .chat-panel {
    display: none;
    flex-direction: column;
    position: absolute;
    width: 320px;
    max-width: min(320px, calc(100vw - 32px));
    background: var(--speechify-agent-bg);
    border: 1px solid var(--speechify-agent-border);
    box-shadow: var(--speechify-agent-shadow);
    border-radius: 16px;
    overflow: hidden;
    z-index: 10;
    animation: popup-in 200ms ease;
  }
  .chat-panel.open { display: flex; }
  .chat-panel.fab-mode { bottom: calc(100% + 8px); right: 0; }
  .chat-panel.pill-mode { top: calc(100% + 8px); left: 0; min-width: 100%; }
  @keyframes popup-in {
    from { opacity: 0; transform: translateY(8px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .chat-scroll {
    max-height: 320px;
    min-height: 96px;
    overflow-y: auto;
    padding: 12px;
    display: flex;
    flex-direction: column;
    gap: 8px;
    scrollbar-width: thin;
    scrollbar-color: var(--speechify-agent-muted) transparent;
  }
  .chat-msg { display: flex; flex-direction: column; gap: 2px; }
  .chat-msg-user { align-items: flex-end; }
  .chat-msg-assistant { align-items: flex-start; }
  .chat-role {
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--speechify-agent-muted);
  }
  .chat-bubble {
    max-width: 85%;
    padding: 6px 10px;
    border-radius: 12px;
    font-size: 13px;
    line-height: 1.4;
    word-break: break-word;
  }
  .chat-bubble-user { background: var(--speechify-agent-accent); color: #ffffff; }
  .chat-bubble-assistant { background: var(--speechify-agent-border); color: var(--speechify-agent-fg); }
  .chat-bubble p { margin: 0 0 6px; }
  .chat-bubble p:last-child { margin-bottom: 0; }
  .chat-bubble a { color: inherit; text-decoration: underline; }
  .chat-thinking { display: none; gap: 4px; padding: 6px 10px; align-self: flex-start; }
  .chat-thinking.show { display: flex; }
  .chat-thinking span {
    width: 5px;
    height: 5px;
    border-radius: 50%;
    background: var(--speechify-agent-muted);
    animation: chat-pulse 1.2s ease-in-out infinite;
  }
  .chat-thinking span:nth-child(2) { animation-delay: 0.2s; }
  .chat-thinking span:nth-child(3) { animation-delay: 0.4s; }
  @keyframes chat-pulse {
    0%, 100% { transform: scale(1); opacity: 0.9; }
    50% { transform: scale(1.4); opacity: 0.4; }
  }
  .chat-notice {
    display: none;
    padding: 8px 12px;
    font-size: 12px;
    color: var(--speechify-agent-muted);
    border-top: 1px solid var(--speechify-agent-border);
  }
  .chat-notice.show { display: block; }
  .chat-composer {
    display: flex;
    gap: 8px;
    align-items: flex-end;
    padding: 8px;
    border-top: 1px solid var(--speechify-agent-border);
  }
  .chat-input {
    all: unset;
    box-sizing: border-box;
    flex: 1;
    min-height: 20px;
    max-height: 96px;
    padding: 8px 10px;
    border-radius: 10px;
    background: var(--speechify-agent-border);
    color: var(--speechify-agent-fg);
    font: inherit;
    line-height: 1.4;
    resize: none;
    overflow-y: auto;
  }
  .chat-input:disabled { opacity: 0.5; }
  .chat-send {
    all: unset;
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    border-radius: 10px;
    background: var(--speechify-agent-accent);
    color: #ffffff;
    cursor: pointer;
    flex: none;
  }
  .chat-send:disabled { opacity: 0.4; cursor: default; }
  .chat-composer.hidden { display: none; }
`;

const SEND_ICON = `<svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden="true"><path d="M3.4 20.4 20.85 12 3.4 3.6 3.39 10l12.61 2-12.61 2z"/></svg>`;

export class TextChatPanel {
  #panel: HTMLDivElement;
  #scroll: HTMLDivElement;
  #messagesEl: HTMLDivElement;
  #thinkingEl: HTMLDivElement;
  #notice: HTMLDivElement;
  #composer: HTMLDivElement;
  #input: HTMLTextAreaElement;
  #send: HTMLButtonElement;
  #userScrolledUp = false;
  #onSubmit: ((text: string) => void) | null = null;

  constructor(shadowRoot: ShadowRoot) {
    const style = document.createElement("style");
    style.textContent = CHAT_CSS;
    shadowRoot.appendChild(style);

    this.#panel = document.createElement("div");
    this.#panel.className = "chat-panel";

    this.#scroll = document.createElement("div");
    this.#scroll.className = "chat-scroll";
    // In text mode the thread is the only output channel, so mark it a live
    // log: assistive tech announces each new turn as it's appended.
    this.#scroll.setAttribute("role", "log");
    this.#scroll.setAttribute("aria-live", "polite");
    this.#scroll.setAttribute("aria-atomic", "false");
    this.#scroll.setAttribute("aria-label", "Chat messages");
    this.#scroll.addEventListener("scroll", () => {
      const el = this.#scroll;
      this.#userScrolledUp = el.scrollHeight - el.scrollTop - el.clientHeight > SCROLL_THRESHOLD;
    });

    this.#messagesEl = document.createElement("div");
    this.#messagesEl.style.display = "contents";

    this.#thinkingEl = document.createElement("div");
    this.#thinkingEl.className = "chat-thinking";
    // Announce the pending reply; the dots themselves are decorative.
    this.#thinkingEl.setAttribute("role", "status");
    this.#thinkingEl.setAttribute("aria-label", "Agent is replying");
    this.#thinkingEl.innerHTML = "<span></span><span></span><span></span>";

    this.#scroll.append(this.#messagesEl, this.#thinkingEl);

    this.#notice = document.createElement("div");
    this.#notice.className = "chat-notice";

    this.#composer = document.createElement("div");
    this.#composer.className = "chat-composer";

    this.#input = document.createElement("textarea");
    this.#input.className = "chat-input";
    this.#input.rows = 1;
    this.#input.setAttribute("placeholder", "Type a message…");
    this.#input.setAttribute("aria-label", "Message");
    this.#input.addEventListener("input", () => this.#autosize());
    this.#input.addEventListener("keydown", (e) => {
      // Enter sends; Shift+Enter inserts a newline.
      if (e.key === "Enter" && !e.shiftKey) {
        e.preventDefault();
        this.#submit();
      }
    });

    this.#send = document.createElement("button");
    this.#send.type = "button";
    this.#send.className = "chat-send";
    this.#send.setAttribute("aria-label", "Send message");
    this.#send.innerHTML = SEND_ICON;
    this.#send.addEventListener("click", () => this.#submit());

    this.#composer.append(this.#input, this.#send);
    this.#panel.append(this.#scroll, this.#notice, this.#composer);
  }

  get element(): HTMLDivElement {
    return this.#panel;
  }

  onSubmit(cb: (text: string) => void): void {
    this.#onSubmit = cb;
  }

  setMode(mode: "pill" | "fab"): void {
    this.#panel.classList.toggle("pill-mode", mode === "pill");
    this.#panel.classList.toggle("fab-mode", mode === "fab");
  }

  show(): void {
    this.#panel.classList.add("open");
  }

  hide(): void {
    this.#panel.classList.remove("open");
  }

  appendMessage(role: "user" | "assistant", text: string): void {
    const wrapper = document.createElement("div");
    wrapper.className = `chat-msg chat-msg-${role}`;

    const roleEl = document.createElement("span");
    roleEl.className = "chat-role";
    roleEl.textContent = role === "user" ? "You" : "Agent";

    const bubble = document.createElement("span");
    bubble.className = `chat-bubble chat-bubble-${role}`;
    // Plain text first, always. The renderer is a deferred chunk (it exists
    // only for assistant turns and the terms modal, so the shell must not pull
    // it in), and a failed chunk load must degrade to readable text rather
    // than an empty bubble. Assistant turns then upgrade in place once it
    // resolves - markdown is presentation, not the message.
    bubble.textContent = text;
    if (role === "assistant") {
      void loadMarkdown()
        .then((render) => {
          bubble.innerHTML = render(text);
          this.#scrollToEndIfPinned();
        })
        .catch(() => {
          // Keep the plain-text bubble already rendered above.
        });
    }

    wrapper.append(roleEl, bubble);
    this.#messagesEl.appendChild(wrapper);
    this.#scrollToEndIfPinned();
  }

  setThinking(thinking: boolean): void {
    this.#thinkingEl.classList.toggle("show", thinking);
    if (thinking) this.#scrollToEndIfPinned();
  }

  setInputEnabled(enabled: boolean): void {
    this.#input.disabled = !enabled;
    this.#send.disabled = !enabled;
  }

  /** Show a terminal notice (ended / unavailable) and remove the composer. */
  setNotice(text: string): void {
    this.#notice.textContent = text;
    this.#notice.classList.add("show");
    this.#composer.classList.add("hidden");
  }

  focusInput(): void {
    if (!this.#input.disabled) this.#input.focus();
  }

  #submit(): void {
    const text = this.#input.value.trim();
    if (!text || this.#input.disabled) return;
    this.#input.value = "";
    this.#autosize();
    this.#onSubmit?.(text);
  }

  #autosize(): void {
    this.#input.style.height = "auto";
    this.#input.style.height = `${Math.min(this.#input.scrollHeight, 96)}px`;
  }

  #scrollToEndIfPinned(): void {
    if (!this.#userScrolledUp) this.#scroll.scrollTop = this.#scroll.scrollHeight;
  }
}
